import React, { useState, useRef, useEffect } from 'react';
import {
  Button,
  TextField,
  Box,
  CircularProgress,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import { ShoppingCart, Info, OpenInNew, AccountBalanceWalletOutlined } from '@mui/icons-material';
import { AlertMessages } from '../AlertMessages';
import { getStatement, getBalance, getWalletStudents } from '../../api/endpoints/wallet';
import { getErrorMessage } from '../../utils/errorHandler';
import { formatCurrency } from '../../utils/formatCoins';

export const StatementForm = () => {
  const [studentCode, setStudentCode] = useState('');
  const [balance, setBalance] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedPixTopup, setSelectedPixTopup] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pixDetailsDialogOpen, setPixDetailsDialogOpen] = useState(false);
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const studentsLoadedRef = useRef(false);
  const isLoadingStudentsRef = useRef(false);
  const [alertState, setAlertState] = useState({
    show: false,
    type: 'success',
    message: '',
  });

  // Sincronizar studentCode quando selectedStudent mudar
  useEffect(() => {
    if (selectedStudent && selectedStudent.studentCode) {
      setStudentCode(selectedStudent.studentCode);
    } else if (!selectedStudent) {
      setStudentCode('');
    }
  }, [selectedStudent]);

  const loadStudents = async () => {
    // Evitar carregar múltiplas vezes
    if (studentsLoadedRef.current || isLoadingStudentsRef.current) {
      return;
    }

    try {
      isLoadingStudentsRef.current = true;
      setStudentsLoading(true);
      const data = await getWalletStudents();
      setStudents(data.students || []);
      studentsLoadedRef.current = true;
    } catch (error) {
      setAlertState({
        show: true,
        type: 'error',
        message: getErrorMessage(error),
      });
    } finally {
      setStudentsLoading(false);
      isLoadingStudentsRef.current = false;
    }
  };

  const handleSearch = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    const codeToSearch = selectedStudent?.studentCode || studentCode.trim();
    
    if (!codeToSearch) {
      setAlertState({
        show: true,
        type: 'error',
        message: 'Selecione um estudante',
      });
      return;
    }

    try {
      setLoading(true);
      const [statementData, balanceData] = await Promise.all([
        getStatement(codeToSearch.toUpperCase()),
        getBalance(codeToSearch.toUpperCase()),
      ]);

      setEntries(statementData.entries || []);
      setBalance(balanceData.balance);
    } catch (error) {
      setEntries([]);
      setBalance(null);
      setAlertState({
        show: true,
        type: 'error',
        message: getErrorMessage(error),
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getRefTypeLabel = (refType) => {
    const labels = {
      ORDER: 'Compra',
      MANUAL_TOPUP: 'Recarga',
      REVERSAL: 'Estorno',
      PIX_TOPUP: 'Recarga PIX',
    };
    return labels[refType] || refType;
  };

  const getRefTypeColor = (refType) => {
    const colors = {
      ORDER: 'error',
      MANUAL_TOPUP: 'success',
      REVERSAL: 'warning',
    };
    return colors[refType] || 'default';
  };

  const getDirectionSign = (direction) => {
    return direction === '+' ? '+' : '-';
  };

  const getDirectionColor = (direction) => {
    return direction === '+' ? '#2e7d32' : '#d32f2f';
  };

  const handleOpenOrderDetails = (entry) => {
    if (entry.order && entry.order.items) {
      setSelectedOrder(entry.order);
      setDialogOpen(true);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedOrder(null);
  };

  const handleOpenPixDetails = (entry) => {
    if (entry.pixTopup) {
      setSelectedPixTopup(entry.pixTopup);
      setPixDetailsDialogOpen(true);
    }
  };

  const handleClosePixDetailsDialog = () => {
    setPixDetailsDialogOpen(false);
    setSelectedPixTopup(null);
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 3,
          border: '1px solid #e0e0e0',
          bgcolor: '#fafafa',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#0b4f6c' }}>
              Extrato de Transações
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Consulte o saldo e o histórico de movimentações da carteira do estudante.
            </Typography>
          </Box>

          <Box
            component="form"
            onSubmit={handleSearch}
            sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}
          >
            <Autocomplete
              options={students}
              getOptionLabel={(option) => option?.name || ''}
              value={selectedStudent}
              loading={studentsLoading}
              onOpen={() => {
                if (!studentsLoadedRef.current && !isLoadingStudentsRef.current) {
                  loadStudents();
                }
              }}
              onChange={(_, newValue) => {
                setSelectedStudent(newValue);
                if (newValue && newValue.studentCode) {
                  setStudentCode(newValue.studentCode);
                } else {
                  setStudentCode('');
                }
              }}
              sx={{ minWidth: 280, maxWidth: 380 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label={
                    studentsLoading ? 'Carregando...' : 'Selecione ou busque o estudante'
                  }
                  variant="outlined"
                  onFocus={() => {
                    if (!studentsLoadedRef.current && !isLoadingStudentsRef.current) {
                      loadStudents();
                    }
                  }}
                  sx={{ bgcolor: 'white' }}
                />
              )}
              disabled={studentsLoading}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={loading || (!studentCode.trim() && !selectedStudent?.studentCode)}
              sx={{
                bgcolor: '#1e958c',
                '&:hover': { bgcolor: '#0b4f6c' },
                minWidth: 120,
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Buscar'}
            </Button>
          </Box>
        </Box>

        {balance !== null && selectedStudent && (
          <Paper
            variant="outlined"
            sx={{
              mb: 2,
              p: 2,
              borderRadius: 2,
              bgcolor: '#e8f5e9',
              border: '1px solid rgba(46, 125, 50, 0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <AccountBalanceWalletOutlined sx={{ color: 'success.dark', fontSize: 28 }} />
            <Box>
              <Typography variant="body2" color="text.secondary">
                {selectedStudent.name}
              </Typography>
              <Typography variant="h6" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                Saldo atual: {formatCurrency(balance)}
              </Typography>
            </Box>
          </Paper>
        )}

        {entries.length > 0 ? (
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ maxHeight: 480, borderRadius: 2, bgcolor: '#fff' }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Data/Hora</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Tipo</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>
                    Valor
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>
                    Direção
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Status</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>
                    Detalhes
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((entry) => (
                  <TableRow key={entry.id} hover>
                    <TableCell>{formatDate(entry.createdAt)}</TableCell>
                    <TableCell>
                      <Chip
                        label={getRefTypeLabel(entry.refType)}
                        color={getRefTypeColor(entry.refType)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: getDirectionColor(entry.direction),
                        }}
                      >
                        {getDirectionSign(entry.direction)}
                        {formatCurrency(entry.amountCoins)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={entry.direction === '+' ? 'Entrada' : 'Saída'}
                        size="small"
                        sx={{
                          bgcolor: entry.direction === '+' ? '#e8f5e9' : '#ffebee',
                          color: entry.direction === '+' ? '#2e7d32' : '#d32f2f',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={entry.status === 'POSTED' ? 'Confirmado' : 'Cancelado'}
                        size="small"
                        color={entry.status === 'POSTED' ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {entry.refType === 'ORDER' && entry.order && entry.order.items ? (
                        <IconButton
                          size="small"
                          onClick={() => handleOpenOrderDetails(entry)}
                          sx={{
                            color: '#1e958c',
                            '&:hover': { color: '#0b4f6c', bgcolor: 'rgba(30, 149, 140, 0.1)' },
                          }}
                          title="Ver itens comprados"
                        >
                          <ShoppingCart fontSize="small" />
                        </IconButton>
                      ) : entry.refType === 'PIX_TOPUP' && entry.pixTopup ? (
                        <IconButton
                          size="small"
                          onClick={() => handleOpenPixDetails(entry)}
                          sx={{
                            color: '#1e958c',
                            '&:hover': { color: '#0b4f6c', bgcolor: 'rgba(30, 149, 140, 0.1)' },
                          }}
                          title="Ver detalhes do pagamento PIX"
                        >
                          <Info fontSize="small" />
                        </IconButton>
                      ) : (
                        <IconButton size="small" disabled>
                          <Info fontSize="small" sx={{ color: '#ccc' }} />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          !loading &&
          (selectedStudent?.studentCode || studentCode.trim()) &&
          balance !== null && (
            <Paper
              variant="outlined"
              sx={{
                py: 4,
                px: 2,
                borderRadius: 2,
                bgcolor: '#fff',
                textAlign: 'center',
              }}
            >
              <Typography color="text.secondary">
                Nenhuma transação encontrada para este estudante.
              </Typography>
            </Paper>
          )
        )}

        {!loading && balance === null && entries.length === 0 && (
          <Paper
            variant="outlined"
            sx={{
              py: 6,
              px: 2,
              borderRadius: 2,
              bgcolor: '#fff',
              textAlign: 'center',
            }}
          >
            <Typography color="text.secondary">
              Selecione um estudante e clique em Buscar para ver o extrato e o saldo.
            </Typography>
          </Paper>
        )}
      </Paper>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: '#1e958c',
            color: 'white',
            fontWeight: 600,
            py: 2,
          }}
        >
          Itens Comprados
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedOrder && (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Data da Compra
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {formatDate(selectedOrder.createdAt)}
                </Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              <List>
                {selectedOrder.items.map((item, index) => (
                  <React.Fragment key={item.id}>
                    <ListItem
                      sx={{
                        px: 0,
                        py: 1.5,
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body1" fontWeight={500}>
                            {item.productName || `Produto #${item.productId}`}
                          </Typography>
                        }
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="body2" color="textSecondary">
                              Quantidade: {item.qty} × {formatCurrency(item.unitPriceCoins)} ={' '}
                              <strong>{formatCurrency(item.lineTotalCoins)}</strong>
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < selectedOrder.items.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
              <Divider sx={{ my: 2 }} />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  pt: 1,
                }}
              >
                <Typography variant="h6" fontWeight={600}>
                  Total:
                </Typography>
                <Typography variant="h6" fontWeight={600} color="#1e958c">
                  {formatCurrency(selectedOrder.totalCoins)}
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseDialog}
            variant="contained"
            sx={{
              bgcolor: '#1e958c',
              '&:hover': { bgcolor: '#0b4f6c' },
            }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={pixDetailsDialogOpen}
        onClose={handleClosePixDetailsDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: '#1e958c',
            color: 'white',
            fontWeight: 600,
            py: 2,
          }}
        >
          Detalhes do Pagamento PIX
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedPixTopup && (
            <>
              {/* Informações Principais - Destaque */}
              <Box sx={{ mb: 3, p: 2, bgcolor: '#e8f5e9', borderRadius: 2 }}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Valor da Recarga
                  </Typography>
                  <Typography variant="h6" fontWeight={600} color="#2e7d32">
                    R$ {parseFloat(selectedPixTopup.amountReais || 0).toFixed(2).replace('.', ',')}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Status do Pagamento
                  </Typography>
                  <Chip
                    label={
                      selectedPixTopup.status === 'PAID'
                        ? 'Pago'
                        : selectedPixTopup.status === 'PENDING'
                        ? 'Pendente'
                        : selectedPixTopup.status === 'EXPIRED'
                        ? 'Expirado'
                        : selectedPixTopup.status === 'CANCELLED'
                        ? 'Cancelado'
                        : selectedPixTopup.status
                    }
                    color={selectedPixTopup.status === 'PAID' ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
                {selectedPixTopup.paidAt && (
                  <Box>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Data da Transação
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {formatDate(selectedPixTopup.paidAt)}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Identificadores para Rastreamento */}
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2, color: '#666' }}>
                Identificadores para Rastreamento
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Order NSU (Identificador Principal)
                </Typography>
                <Typography variant="body1" fontWeight={500} sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {selectedPixTopup.orderNsu}
                </Typography>
              </Box>

              {selectedPixTopup.transactionNsu && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Transaction NSU (InfinitePay)
                  </Typography>
                  <Typography variant="body1" fontWeight={500} sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {selectedPixTopup.transactionNsu}
                  </Typography>
                </Box>
              )}

              {selectedPixTopup.invoiceSlug && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Invoice Slug
                  </Typography>
                  <Typography variant="body1" fontWeight={500} sx={{ fontFamily: 'monospace' }}>
                    {selectedPixTopup.invoiceSlug}
                  </Typography>
                </Box>
              )}

              {/* Extrair E2E ID e Transaction ID do metadata se disponível */}
              {selectedPixTopup.metadata && (
                <>
                  {selectedPixTopup.metadata.e2eId && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        Chave PIX (E2E ID)
                      </Typography>
                      <Typography variant="body1" fontWeight={500} sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                        {selectedPixTopup.metadata.e2eId}
                      </Typography>
                    </Box>
                  )}
                  {selectedPixTopup.metadata.transactionId && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        ID Transação InfinitePay
                      </Typography>
                      <Typography variant="body1" fontWeight={500} sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                        {selectedPixTopup.metadata.transactionId}
                      </Typography>
                    </Box>
                  )}
                </>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Informações Adicionais */}
              {selectedPixTopup.expiresAt && selectedPixTopup.status === 'PENDING' && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Expira em
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {formatDate(selectedPixTopup.expiresAt)}
                  </Typography>
                </Box>
              )}

              {selectedPixTopup.receiptUrl && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mb: 1 }}>
                    Comprovante de Pagamento
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<OpenInNew />}
                    href={selectedPixTopup.receiptUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      borderColor: '#1e958c',
                      color: '#1e958c',
                      '&:hover': {
                        borderColor: '#0b4f6c',
                        bgcolor: 'rgba(30, 149, 140, 0.1)',
                      },
                    }}
                  >
                    Abrir Comprovante Completo
                  </Button>
                </Box>
              )}

              {/* Mostrar metadata apenas se tiver informações úteis além dos campos já exibidos */}
              {selectedPixTopup.metadata && 
               Object.keys(selectedPixTopup.metadata).filter(
                 key => !['e2eId', 'transactionId', 'orderNsu', 'transactionNsu', 'invoiceSlug', 'paidAmount', 'receiptUrl'].includes(key)
               ).length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Informações Técnicas Adicionais
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        bgcolor: '#f5f5f5',
                        p: 1.5,
                        borderRadius: 1,
                        fontSize: '0.75rem',
                        overflow: 'auto',
                        maxHeight: 150,
                        fontFamily: 'monospace',
                      }}
                    >
                      {JSON.stringify(selectedPixTopup.metadata, null, 2)}
                    </Box>
                  </Box>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleClosePixDetailsDialog}
            variant="contained"
            sx={{
              bgcolor: '#1e958c',
              '&:hover': { bgcolor: '#0b4f6c' },
            }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <AlertMessages
        show={alertState.show}
        typeMessage={alertState.type}
        contentMessage={alertState.message}
        setShow={(show) => setAlertState((prev) => ({ ...prev, show }))}
      />
    </>
  );
};
